import { Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { NavigationEnd, Router } from '@angular/router';
import { LDF_CONFIGURABLE_SETTINGS, LDF_SHOW_ENV_WARNING_BANNER } from '@app/constants';
import { AuthService } from '@app/service/auth.service';
import { HeaderService } from '@data/services/header/header.service';
import { UserService } from '@data/services/user/user.service';
import { environment } from '@env/environment';
import { ImportantNoticeComponent } from '@modal/header-modals/important-notice/important-notice.component';
import { OktaEmailSelectionComponent } from '@modal/header-modals/okta-email-selection/okta-email-selection.component';
import { NotificationComponent } from '@modal/header-modals/notification/notification.component';
import { QuickSearchComponent } from '@modal/header-modals/quick-search/quick-search.component';
import { UserProfileComponent } from '@modal/header-modals/user-profile/user-profile.component';
import { ConfigurableSettingsComponent } from '@modules/configurable-settings/configurable-settings/configurable-settings.component';
import { PhoneDialerUserComponent } from '@modules/integration/components/phone-dialer-user/phone-dialer-user.component';
import { ConfirmDeleteComponent } from '@shared/modals/confirm-delete/confirm-delete.component';
import { CommonService } from '@shared/services/common/common.service';
import { LaunchdarklyService } from '@shared/services/LaunchDarkly/launchdarkly.service';
import { ToastMsgService } from '@shared/services/toastr/toast-msg.service';
import moment from 'moment';
import { CookieService } from 'ngx-cookie-service';
import { filter, first, interval, map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);

  private _pollingInterval: number = 120000;
  private _userId: string;
  private _isDismissedNotify: boolean = false;
  private _isNotifyDialog: boolean = false;
  private _isOktaDialogOpen: boolean = false;

  public readonly configurableSettingsFlag = toSignal(
    this._launchDarklySvc.getFlag(LDF_CONFIGURABLE_SETTINGS).pipe(map(Boolean)),
    { initialValue: false }
  );
  public readonly showEnvBanner = toSignal(
    this._launchDarklySvc.getFlag(LDF_SHOW_ENV_WARNING_BANNER, true).pipe(map(Boolean)),
    { initialValue: false }
  );
  public readonly showPhoneDialer = toSignal(
    this._commonService.accountSettings$.pipe(
      map(accountSettings => accountSettings?.phone_dialer_provider_active === '1')
    ),
    { initialValue: false }
  );
  public readonly userName = toSignal(
    this.authService.userDetails$.pipe(
      map(userDetails => userDetails ? `${userDetails.fname} ${userDetails.lname}` : '')
    ),
    { initialValue: '' }
  );

  public hasPeproAccess: boolean = false;
  public isMimickedUser: boolean = false;
  public isDisabled: boolean = false;
  public isExpanded = true;
  public isLoading: boolean = true;
  public isLoadingNotifications: boolean = false;
  public isSubMenuLoading: boolean = true;
  public showHeader: boolean;
  public showSubmenu: boolean = false;

  public date: any;
  public month: any;
  public year: any;
  public day: any;
  public time: any;

  public notification_count: number = 0;

  @ViewChild('sidenav') sidenav: MatSidenavModule;

  constructor(
    private headerService: HeaderService,
    private commonService: CommonService,
    private authService: AuthService,
    private router: Router,
    private cookieService: CookieService,
    private toastMsgService: ToastMsgService,
    private userService: UserService,
    private _launchDarklySvc: LaunchdarklyService,
    public dialog: MatDialog,
    private _commonService: CommonService
  ) {
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(event => event instanceof NavigationEnd),
        first(),
      ).subscribe(navigation => {
        const url = (navigation as NavigationEnd).url
        this.showHeader = url.indexOf('registration') < 0;

        if (this.showHeader) {
          this._userId = this.authService.getToken().user_id;

          this._checkPeproAccess();
          this._checkMimickedUser();
          this._getUserDetails();
          this._notificationCount();

          //Polling after every 2 mins
          interval(this._pollingInterval)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this._getNotificationCount();
            });

          interval(1000)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this._currentDateTime();
            });

        }

      });

    let details = navigator.userAgent;
    let regexp = /android|iphone|kindle|ipad/i;
    let isMobileDevice = regexp.test(details);
    if (isMobileDevice) {
      this.isDisabled = true;
      this.isExpanded = false;
    } else {
      this.isDisabled = false;
      this.isExpanded = true;
    }
    this.toastMsgService.isExpanded = this.isExpanded;
  }

  @HostListener('window:resize', ['$event'])
  public onResize(event:any): void {
    if (this.sidenav !== undefined) {
      if (event.target.innerWidth <= 980) { // This can be any value.
        this.isExpanded = false;
      } else {
        this.isExpanded = true;
      }
      this.commonService.setMenuExpand(this.isExpanded);
      this.toastMsgService.isExpanded = this.isExpanded;
    }
  }

  private _notificationCount() {
    this.headerService.getNotifyAlert()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response) {
            if(!this._isDismissedNotify) {
              this._getNotificationCount();
            }
            this._isDismissedNotify = true;
          }
        },
        error: (err) => { },
        complete: () => { }
      })
  }

  public ngOnInit(): void {
    this._currentDateTime();
  }

  //Check if user has pepro link access
  private _checkPeproAccess() {
    this.headerService.getPeproAccess()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (response: any) => {
          if (response.result === "True") {
            this.hasPeproAccess = true;
          }
        }
      );
  }

  private _checkMimickedUser() {
    const adminAuthCookie = this.cookieService.get('LAAuth2LAWebAdmin');
    const adminAuth = adminAuthCookie ? JSON.parse(decodeURIComponent(adminAuthCookie)) : null;
    if (adminAuth?.admin_user_id) {
      this.isMimickedUser = Number.parseInt(this.authService.getToken().user_id) !== Number.parseInt(adminAuth?.admin_user_id);
      if(!this.isMimickedUser){
        this.authService.deleteMimicAdminCookie();
    }
    }
  }

  private _currentDateTime() {
    let date = new Date();
    this.date = date.getDate();
    this.month = this.getFormattedValue('MMMM');
    this.day = this.getFormattedValue('dddd');
    this.year = this.getFormattedValue('YYYY');
    this.time = this.getFormattedValue("hh:mm a");
  }

  private getFormattedValue(format: string) {
    return moment().format(format);
  }

  public expandMenu() {
    this.isExpanded = !this.isExpanded;
    this.commonService.setMenuExpand(this.isExpanded);
    this.toastMsgService.isExpanded = this.isExpanded;
  }

  public openMenu() {
    this.isSubMenuLoading = true;
    this.showSubmenu = true;

    this._commonService.getLoggedInAccountSettings(this.commonService.getAccountId(), true)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSubMenuLoading = false;
        }
      });

  }

  public openUserProfile() {
    this.dialog.open(UserProfileComponent, {
      width: '100%',
      autoFocus: false,
      data: {
        user_id: this._userId
      },
    });
  }

  public openConfigurableSettings() {
    this.dialog.open(ConfigurableSettingsComponent, {
      width: '70%',
      data: {
        'parent': 'header'
      }
    });
  }

  public openPepro() {
    const laWebCookie = this.authService.getAuthCookie();

    // Set PEP cookie
    this.cookieService.set(
      'LAWeb2PEWeb', laWebCookie,
      { path: '/', domain: '.leadadvantagepro.com', secure: true }
    )

    window.open(environment.PEPro, '_openPEProLink');
  }

  public openCallRecordingUserSettings() {
    this.dialog.open(PhoneDialerUserComponent, {
      width: '55%',
      autoFocus: true
    });

  }

  /* to open the search dialog */
  public openSearchDialog() {
    window.scroll(0, 0);
    this.dialog.open(QuickSearchComponent, {
      width: '60%',
      autoFocus: false,
    })
  }

  private _getUserDetails() {
    this.userService.getUserDetailsAndPermissions(this.authService.getToken().user_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (userDetailsResponse) => {
          const userDetails = userDetailsResponse.body!;

          this.isLoading = false;

          this.authService.user_details = userDetails;
          this.authService.userDetails$.next(userDetails);
          this.authService.account_id = userDetails.account_id;

          this.commonService.setAccountId(userDetails.account_id);

          this.authService.user_details['rolePermission'] = JSON.parse(userDetailsResponse.headers.get('x-lapro-acl-array') || '[]');
          this.authService.user_details['hash'] = userDetailsResponse.headers.get('x-lapro-acl');

          this._promptForOktaEmailSelection(userDetails);

          // this._getCobrandDetails(userDetails.account_id);
          this._commonService.getLoggedInAccountSettings(userDetails.account_id)
            .pipe(first(), takeUntilDestroyed(this.destroyRef))
            .subscribe();
        },
        error: (err) => { },
        complete: () => { }
      });
  }

  private _promptForOktaEmailSelection(userDetails: any): void {
    this.userService.getOktaCredentialStatus(userDetails.user_id)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (credentialStatus) => {
          if (credentialStatus || this._isOktaDialogOpen) {
            return;
          }

          this._isOktaDialogOpen = true;

          this.dialog.open(OktaEmailSelectionComponent, {
            width: '75%',
            autoFocus: false,
            data: {
              userId: userDetails.user_id,
              defaultEmail: userDetails.default_email?.email
            }
          }).afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
              this._isOktaDialogOpen = false;
            });
        },
        error: () => {}
      });
  }


  /* notification functionality */
  private _getNotificationCount() {
    this.headerService.getNotificationCount()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.notification_count = response.notice_outstanding_count;
          this.commonService.tempPollingInterval = this._pollingInterval;
          if (response['includes_important'] == '1') {
            this.headerService.getUserNotice(0, 'quickNotify')
              .pipe(first(), takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (response: any) => {
                  if (response?.length) {
                    if(this._isNotifyDialog){
                      return;
                    }
                    this._isNotifyDialog = true;

                    this.dialog.open(ImportantNoticeComponent, {
                      width: '60%',
                      data: response
                    }).afterClosed()
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe((elem:any)=>{
                        this._isDismissedNotify = false;
                        this._isNotifyDialog = false;
                      })
                  }
                },
                error: (err) => {
                },
                complete: () => { }
              })
          }
        },
        error: (err) => { },
        complete: () => { }
      });
  }

  public openNotification() {
    this.isLoadingNotifications = true;
    this.headerService.getUserNotice(0, 'openNotify')
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          let data: any;
          if (response.result == '') {
            data = 'No result';
          } else {
            data = response;
          }
          this.dialog.open(NotificationComponent, {
            width: '65%',
            data: data,
          });
        },
        complete: () => {
          this.isLoadingNotifications = false;
        }
      });
  }

  public logout(): void {
    this.dialog.open(ConfirmDeleteComponent, {
      width: '30%',
      data: {
        'text': 'Are you sure you want to logout?'
      }
    }).afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
      if (res) {
        this.authService.killCookie();
        this.dialog.closeAll();
        localStorage.removeItem('printSettingsCheck');
        this.authService.redirectToAuth();
      }
    });
  }

  public exitMimic(): void {
    const adminAuthCookie = this.cookieService.get('LAAuth2LAWebAdmin');
    const adminAuth = adminAuthCookie ? JSON.parse(decodeURIComponent(adminAuthCookie)) : null;
    if (adminAuth?.admin_user_id) {
      this.dialog.open(ConfirmDeleteComponent, {
        width: '30%',
        data: {
          'text': 'Are you sure you want to exit mimic mode?'
        }
      }).afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(res => {
        if (res) {
          this.userService.mimicUser(adminAuth?.admin_user_id)
            .pipe(first(), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (response: any) => {
                if (response && response.access_token) {
                  this.userService.redirectMimicUser(response.access_token);
                }
              },
              error: (err) => {
                this.toastMsgService.showError(err.error.message);
              },
              complete: () => {}
            });
        }
      });
    }
  }

  public isProd() {
    return environment.production;
  }
}
